import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  RiAddLine,
  RiCloseLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiCalendarLine,
  RiFileTextLine,
  RiUserLine,
  RiTeamLine,
  RiNewspaperLine,
  RiCalculatorLine,
  RiListCheck2,
  RiArrowDownSLine,
  RiHistoryLine,
  RiBankLine,
  RiWalletLine,
} from "react-icons/ri";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import {
  generateReleaseOrderPDF,
  RO_IMAGE_BASE64,
} from "../Util/GenerateReleaseOrderPDF";
import { RiDownloadLine } from "react-icons/ri";

const BASE_URL = process.env.REACT_APP_API_URL;

const Spinner = ({ className = "w-4 h-4" }) => (
  <AiOutlineLoading3Quarters className={`animate-spin ${className}`} />
);

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div
      className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium
      ${
        type === "success"
          ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
          : "bg-red-50 border border-red-200 text-red-800"
      }`}
    >
      {type === "success" ? (
        <RiCheckLine className="w-4 h-4 text-emerald-500" />
      ) : (
        <RiErrorWarningLine className="w-4 h-4 text-red-500" />
      )}
      <span>{message}</span>
      <button
        onClick={onClose}
        className="ml-2 opacity-50 hover:opacity-100 transition"
      >
        <RiCloseLine className="w-4 h-4" />
      </button>
    </div>
  );
};

const tomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0];
};

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const emptyItem = (nextRONumber = "") => ({
  id: Math.random().toString(36).slice(2),
  media: "",
  publication: "",
  edition: "",
  hue: "B/W",
  width: "",
  height: "",
  dop: tomorrow(),
  pagePosition: "",
  adType: "",
  selectedAdName: "",
  rate: "",
  roNumber: nextRONumber,
  agencyCommission: 15,
  otherDiscount: 0,
  otherPremium: 0,
  discountType: "discount",
  cashDiscount: 0,
  gst: 5,
  chequeDetails: {
    bankName: "",
    chequeNo: "",
    chequeDate: "",
  },
});

const defaultForm = {
  to: "The Advertisement Manager",
  client: "",
  caption: "",
  staffOrAgent: "staff",
  staffAgentId: "",
  date: tomorrow(),
  items: [],
};

function calcItemAmounts(item) {
  const sqcm = Number(item.width || 0) * Number(item.height || 0);
  const gross = sqcm * Number(item.rate || 0);
  const agencyComm = (gross * Number(item.agencyCommission || 0)) / 100;

  let otherAmount = 0;
  if (item.discountType === "discount") {
    otherAmount = -Number(item.otherDiscount || 0);
  } else {
    otherAmount = Number(item.otherPremium || 0);
  }

  const afterComm = gross - agencyComm + otherAmount;
  const cashDisc = (afterComm * Number(item.cashDiscount || 0)) / 100;
  const taxable = afterComm - cashDisc;
  const gstAmt = (taxable * Number(item.gst || 0)) / 100;
  const net = taxable + gstAmt;

  return {
    sqcm,
    gross,
    agencyComm,
    otherDiscount: item.discountType === "discount" ? item.otherDiscount : 0,
    otherPremium: item.discountType === "premium" ? item.otherPremium : 0,
    cashDisc,
    gstAmt,
    net,
  };
}

function calcTotals(items) {
  return items.reduce(
    (acc, item) => {
      const a = calcItemAmounts(item);
      acc.gross += a.gross;
      acc.agencyComm += a.agencyComm;
      acc.otherDiscount += a.otherDiscount;
      acc.otherPremium += a.otherPremium;
      acc.cashDisc += a.cashDisc;
      acc.gstAmt += a.gstAmt;
      acc.net += a.net;
      return acc;
    },
    {
      gross: 0,
      agencyComm: 0,
      otherDiscount: 0,
      otherPremium: 0,
      cashDisc: 0,
      gstAmt: 0,
      net: 0,
    },
  );
}

const Label = ({ children, className = "", required = false }) => (
  <label
    className={`text-[10px] font-semibold text-blue-600 uppercase tracking-widest mb-1 block ${className}`}
  >
    {children}
    {required && <span className="text-red-500 ml-1">*</span>}
  </label>
);

const inputCls =
  "w-full bg-blue-50/60 border border-blue-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition";

const selectCls =
  "w-full bg-[#3d2626] border border-[#3d2626] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition appearance-none cursor-pointer";

const SelectWrapper = ({ children }) => (
  <div className="relative">
    {children}
    <RiArrowDownSLine className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white pointer-events-none" />
  </div>
);

function ItemFinancials({ item }) {
  const amounts = calcItemAmounts(item);

  return (
    <div className="bg-blue-50/40 border border-blue-100 rounded-xl p-4 space-y-2.5 text-sm">
      <div className="text-[10px] font-bold text-blue-500 uppercase tracking-widest mb-3">
        Financial Summary
      </div>

      <div className="flex items-center justify-between py-1.5 border-b border-blue-100">
        <span className="text-slate-500 text-xs">Gross Amount</span>
        <span className="font-bold text-slate-800 font-mono text-sm">
          ₹{fmt(amounts.gross)}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-slate-500 text-xs flex-1">Agency Commission</span>
        <div className="flex items-center gap-1.5">
          <input
            type="number"
            min="0"
            max="100"
            value={item.agencyCommission}
            onChange={(e) => item._onChange("agencyCommission", e.target.value)}
            className="w-12 text-center border border-blue-200 rounded-lg py-1 text-xs bg-white focus:outline-none focus:border-blue-500 transition"
          />
          <span className="text-[10px] text-blue-400">%</span>
          <span className="font-semibold text-red-500 font-mono text-xs w-20 text-right">
            − ₹{fmt(amounts.agencyComm)}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-slate-500 text-xs flex-1">Other</span>
        <div className="flex items-center gap-1.5 flex-wrap">
          <div className="relative">
            <select
              value={item.discountType}
              onChange={(e) => item._onChange("discountType", e.target.value)}
              className="border border-blue-200 rounded-lg px-2 py-1 text-xs bg-white text-slate-700 focus:outline-none focus:border-blue-500 appearance-none pr-5 cursor-pointer"
            >
              <option value="discount">Discount</option>
              <option value="premium">Premium</option>
            </select>
            <RiArrowDownSLine className="absolute right-0.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
          </div>
          <input
            type="number"
            min="0"
            value={
              item.discountType === "discount"
                ? item.otherDiscount
                : item.otherPremium
            }
            onChange={(e) => {
              if (item.discountType === "discount") {
                item._onChange("otherDiscount", e.target.value);
              } else {
                item._onChange("otherPremium", e.target.value);
              }
            }}
            className="w-16 text-center border border-blue-200 rounded-lg py-1 text-xs bg-white focus:outline-none focus:border-blue-500 transition"
          />
          <span className="text-[10px] text-blue-400">Rs.</span>
          <span
            className={`font-semibold font-mono text-xs w-20 text-right ${
              item.discountType === "discount"
                ? "text-red-500"
                : "text-green-500"
            }`}
          >
            {item.discountType === "discount" ? "−" : "+"} ₹
            {fmt(
              item.discountType === "discount"
                ? amounts.otherDiscount
                : amounts.otherPremium,
            )}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-slate-500 text-xs flex-1">Cash Discount</span>
        <div className="flex items-center gap-1.5">
          <input
            type="number"
            min="0"
            max="100"
            value={item.cashDiscount}
            onChange={(e) => item._onChange("cashDiscount", e.target.value)}
            className="w-12 text-center border border-blue-200 rounded-lg py-1 text-xs bg-white focus:outline-none focus:border-blue-500 transition"
          />
          <span className="text-[10px] text-blue-400">%</span>
          <span className="font-semibold text-red-500 font-mono text-xs w-20 text-right">
            − ₹{fmt(amounts.cashDisc)}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-slate-500 text-xs flex-1">GST</span>
        <div className="flex items-center gap-1.5">
          <input
            type="number"
            min="0"
            max="100"
            value={item.gst}
            onChange={(e) => item._onChange("gst", e.target.value)}
            className="w-12 text-center border border-blue-200 rounded-lg py-1 text-xs bg-white focus:outline-none focus:border-blue-500 transition"
          />
          <span className="text-[10px] text-blue-400">%</span>
          <span className="font-semibold text-emerald-600 font-mono text-xs w-20 text-right">
            + ₹{fmt(amounts.gstAmt)}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2.5 border-t-2 border-blue-600/20 mt-1">
        <span className="text-blue-800 font-bold text-xs">Net Payable</span>
        <span className="font-extrabold text-blue-700 text-base font-mono">
          ₹{fmt(amounts.net)}
        </span>
      </div>
    </div>
  );
}

export default function CreateReleaseOrder() {
  const [form, setForm] = useState(defaultForm);
  const [activeTab, setActiveTab] = useState(0);
  const [clients, setClients] = useState([]);
  const [media, setMedia] = useState([]);
  const [staff, setStaff] = useState([]);
  const [agents, setAgents] = useState([]);
  const [nextRONumber, setNextRONumber] = useState("");
  const [previousRONumber, setPreviousRONumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [lastCreatedRO, setLastCreatedRO] = useState(null);
  const [adTypes, setAdTypes] = useState({});

  const notify = (message, type = "success") => setToast({ message, type });

  const generateNextRONumber = (baseRO) => {
    if (!baseRO) return "";
    const matchWithSuffix = baseRO.match(
      /^(.*\/)(\d+)\(([A-Z])\)(\/\d{2}-\d{2})$/,
    );
    if (matchWithSuffix) {
      const prefix = matchWithSuffix[1];
      const num = matchWithSuffix[2];
      const currentLetter = matchWithSuffix[3];
      const year = matchWithSuffix[4];
      const nextLetter = String.fromCharCode(currentLetter.charCodeAt(0) + 1);
      return `${prefix}${num}(${nextLetter})${year}`;
    }
    const matchWithoutSuffix = baseRO.match(/^(.*\/)(\d+)(\/\d{2}-\d{2})$/);
    if (matchWithoutSuffix) {
      const prefix = matchWithoutSuffix[1];
      const num = matchWithoutSuffix[2];
      const year = matchWithoutSuffix[3];
      return `${prefix}${num}(A)${year}`;
    }
    return "";
  };

  const formatRONumberWithSuffix = (roNumber, suffix = "A") => {
    if (!roNumber) return "";
    const matchWithoutSuffix = roNumber.match(/^(.*\/)(\d+)(\/\d{2}-\d{2})$/);
    if (matchWithoutSuffix) {
      const prefix = matchWithoutSuffix[1];
      const num = matchWithoutSuffix[2];
      const year = matchWithoutSuffix[3];
      return `${prefix}${num}(${suffix})${year}`;
    }
    return roNumber;
  };

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [clientsRes, mediaRes, staffRes, agentsRes, previousRORes] =
          await Promise.allSettled([
            axios.get(`${BASE_URL}/bank/get-banks`),
            axios.get(`${BASE_URL}/media/get-all`),
            axios.get(`${BASE_URL}/admin/all-users`),
            axios.get(`${BASE_URL}/agent/all-agents`),
            axios.get(`${BASE_URL}/releaseOrder/ro-numbers/previous`),
          ]);

        if (clientsRes.status === "fulfilled")
          setClients(
            clientsRes?.value?.data?.data || clientsRes.value.data || [],
          );
        if (mediaRes.status === "fulfilled")
          setMedia(mediaRes?.value?.data?.data || mediaRes.value.data || []);
        if (staffRes.status === "fulfilled") {
          const all = staffRes?.value?.data?.data || staffRes.value.data || [];
          setStaff(all.filter((u) => u.userType === "staff"));
        }
        if (agentsRes.status === "fulfilled")
          setAgents(agentsRes?.value?.data?.data || agentsRes.value.data || []);
        if (previousRORes.status === "fulfilled") {
          const roData = previousRORes.value?.data?.data;
          if (roData) {
            setPreviousRONumber(roData.previous || "None");
            setNextRONumber(roData.next);
            if (roData.next) {
              const firstItemRONumber = formatRONumberWithSuffix(
                roData.next,
                "A",
              );
              const firstItem = emptyItem(firstItemRONumber);
              setForm((f) => ({ ...f, items: [firstItem] }));
            }
          }
        }
      } catch (error) {
        console.error("Fetch error:", error);
        notify("Some data failed to load", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const fetchAdTypesForMedia = async (mediaId) => {
    if (!mediaId) return;
    try {
      const res = await axios.get(`${BASE_URL}/media/adtypes/${mediaId}`);
      if (res.data?.success && res.data?.adTypes) {
        setAdTypes((prev) => ({
          ...prev,
          [mediaId]: res.data.adTypes,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch ad types:", error);
    }
  };

  const setFormField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const setItemChequeDetail = (itemId, key, val) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((it) =>
        it.id === itemId
          ? { ...it, chequeDetails: { ...it.chequeDetails, [key]: val } }
          : it,
      ),
    }));

  const setItem = (id, key, val) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((it) => (it.id === id ? { ...it, [key]: val } : it)),
    }));

  const addItem = () => {
    if (form.items.length >= 26) {
      notify("Maximum 26 items allowed", "error");
      return;
    }

    let newRONumber = "";

    if (form.items.length === 0) {
      if (nextRONumber) {
        newRONumber = formatRONumberWithSuffix(nextRONumber, "A");
      }
    } else if (form.items.length > 0) {
      const lastItem = form.items[form.items.length - 1];
      if (lastItem.roNumber) {
        newRONumber = generateNextRONumber(lastItem.roNumber);
      }
    }

    if (!newRONumber && nextRONumber) {
      newRONumber = formatRONumberWithSuffix(nextRONumber, "A");
    }

    const newItem = emptyItem(newRONumber);
    setForm((f) => ({ ...f, items: [...f.items, newItem] }));
    setActiveTab(form.items.length);
  };

  const removeItem = (id) => {
    const idx = form.items.findIndex((it) => it.id === id);
    setForm((f) => ({ ...f, items: f.items.filter((it) => it.id !== id) }));
    setActiveTab(Math.max(0, idx - 1));
  };

  const handleMediaChange = async (itemId, mediaId) => {
    const found = media.find((m) => m._id === mediaId);
    setItem(itemId, "media", mediaId);
    setItem(itemId, "adType", "");
    setItem(itemId, "selectedAdName", "");
    await fetchAdTypesForMedia(mediaId);
    if (found?.pricePerSqCm) {
      setItem(itemId, "rate", found.pricePerSqCm);
    }
    if (found?.organisation) {
      setItem(itemId, "publication", found.organisation);
    }
  };

  const handleAdTypeChange = (itemId, selectedAdTypeId) => {
    const currentItem = form.items.find((item) => item.id === itemId);
    const mediaId = currentItem?.media;
    const selectedAdType = adTypes[mediaId]?.find(
      (at) => at._id === selectedAdTypeId,
    );
    setItem(itemId, "adType", selectedAdTypeId);
    setItem(itemId, "selectedAdName", "");
    if (selectedAdType?.price) {
      setItem(itemId, "rate", selectedAdType.price);
    }
  };

  const handleAdNameSelect = (itemId, adName, selectedAdType) => {
    setItem(itemId, "selectedAdName", adName);
    if (selectedAdType?.price) {
      setItem(itemId, "rate", selectedAdType.price);
    }
  };

  const handleSATypeChange = (val) => {
    setForm((f) => ({ ...f, staffOrAgent: val, staffAgentId: "" }));
  };

  // ─── Core submit logic (called after modal confirms "No") ─────────────────
  const doSubmit = async () => {
    if (!form.client) return notify("Please select a client", "error");
    if (!form.staffAgentId)
      return notify("Please select a staff/agent", "error");
    if (form.items.length === 0)
      return notify("Please add at least one item", "error");

    for (let i = 0; i < form.items.length; i++) {
      const item = form.items[i];

      if (!item.edition) {
        notify(`Please enter edition for Item ${i + 1}`, "error");
        setActiveTab(i);
        return;
      }
      if (!item.selectedAdName) {
        notify(`Please select an ad name for Item ${i + 1}`, "error");
        setActiveTab(i);
        return;
      }
      if (!item.roNumber) {
        notify(`RO number is required for Item ${i + 1}`, "error");
        setActiveTab(i);
        return;
      }
      const roMatch = item.roNumber.match(
        /^(.*\/)(\d+)(\([A-Z]\))?(\/\d{2}-\d{2})$/,
      );
      if (!roMatch) {
        notify(
          `Item ${i + 1}: RO number must follow format: NG/1209/25-26 or NG/1209(A)/25-26`,
          "error",
        );
        setActiveTab(i);
        return;
      }
      if (!item.width || item.width <= 0) {
        notify(`Please enter valid width for Item ${i + 1}`, "error");
        setActiveTab(i);
        return;
      }
      if (!item.height || item.height <= 0) {
        notify(`Please enter valid height for Item ${i + 1}`, "error");
        setActiveTab(i);
        return;
      }
      if (!item.rate || item.rate <= 0) {
        notify(`Please enter valid rate for Item ${i + 1}`, "error");
        setActiveTab(i);
        return;
      }
    }

    setSubmitting(true);
    try {
      const clientObj = clients.find((c) => c._id === form.client);
      const saList = form.staffOrAgent === "staff" ? staff : agents;
      const saObj = saList.find((s) => s._id === form.staffAgentId);

      const itemsForPDF = form.items.map(
        ({ id, _onChange, discountType, chequeDetails, ...rest }) => ({
          ...rest,
          width: parseFloat(rest.width) || 0,
          height: parseFloat(rest.height) || 0,
          rate: parseFloat(rest.rate) || 0,
          agencyCommission: parseFloat(rest.agencyCommission) || 0,
          otherDiscount:
            discountType === "discount"
              ? parseFloat(rest.otherDiscount) || 0
              : 0,
          otherPremium:
            discountType === "premium" ? parseFloat(rest.otherPremium) || 0 : 0,
          cashDiscount: parseFloat(rest.cashDiscount) || 0,
          gst: parseFloat(rest.gst) || 0,
          adType: rest.selectedAdName || rest.adType || "",
          chequeDetails: {
            bankName: chequeDetails?.bankName || "",
            chequeNo: chequeDetails?.chequeNo || "",
            chequeDate: chequeDetails?.chequeDate || "",
          },
        }),
      );

      const payload = {
        to: form.to,
        client: form.client,
        caption: form.caption,
        date: form.date,
        items: itemsForPDF,
      };

      if (form.staffOrAgent === "staff") payload.staff = form.staffAgentId;
      else payload.agent = form.staffAgentId;

      await axios.post(`${BASE_URL}/releaseOrder/create`, payload);

      setLastCreatedRO({
        roNumber: itemsForPDF[0]?.roNumber || nextRONumber,
        date: form.date,
        createdAt: new Date().toISOString(),
        caption: form.caption,
        client: clientObj || { bankName: form.client },
        staff: form.staffOrAgent === "staff" ? saObj : undefined,
        agent: form.staffOrAgent === "agent" ? saObj : undefined,
        items: itemsForPDF,
      });

      notify("Release Order created successfully!");

      const refreshRORes = await axios.get(
        `${BASE_URL}/releaseOrder/ro-numbers/previous`,
      );
      if (refreshRORes.data?.success) {
        const roData = refreshRORes.data.data;
        setPreviousRONumber(roData.previous || "None");
        setNextRONumber(roData.next);

        const firstItemRONumber = formatRONumberWithSuffix(roData.next, "A");
        const newItem = emptyItem(firstItemRONumber);
        setForm({
          to: "The Advertisement Manager",
          client: "",
          caption: "",
          staffOrAgent: "staff",
          staffAgentId: "",
          date: tomorrow(),
          items: [newItem],
        });
        setActiveTab(0);
      }
    } catch (error) {
      console.error("Submit error:", error);
      notify(
        error.response?.data?.message || "Failed to create Release Order",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ─── SweetAlert2 confirmation modal before submit ─────────────────────────
  const handleSubmit = async () => {
    const result = await Swal.fire({
      title: "Add Another Item?",
      html: `
        <div style="color:#475569;font-size:14px;line-height:1.6;">
          Would you like to add <strong>another publication item</strong> before creating the Release Order?
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Add Item",
      cancelButtonText: "No, Create RO",
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#16a34a",
      reverseButtons: false,
      focusCancel: true,
      customClass: {
        popup: "swal-ro-popup",
        title: "swal-ro-title",
        confirmButton: "swal-ro-confirm",
        cancelButton: "swal-ro-cancel",
      },
    });

    if (result.isConfirmed) {
      // User clicked "Yes, Add Item" → just add a new item tab
      addItem();
    } else if (result.dismiss === Swal.DismissReason.cancel) {
      // User clicked "No, Create RO" → proceed with submission
      await doSubmit();
    }
    // If dismissed by backdrop/ESC, do nothing
  };

  const resetForm = () => {
    if (nextRONumber) {
      const firstItemRONumber = formatRONumberWithSuffix(nextRONumber, "A");
      const newItem = emptyItem(firstItemRONumber);
      setForm({
        to: "The Advertisement Manager",
        client: "",
        caption: "",
        staffOrAgent: "staff",
        staffAgentId: "",
        date: tomorrow(),
        items: [newItem],
      });
      setActiveTab(0);
      setLastCreatedRO(null);
    }
  };

  const saList = form.staffOrAgent === "staff" ? staff : agents;
  const totals = calcTotals(form.items);
  const activeItem = form.items[activeTab];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      {/* SweetAlert2 custom styles injected globally */}
      <style>{`
        .swal-ro-popup {
          border-radius: 1rem !important;
          padding: 1.75rem !important;
          box-shadow: 0 20px 60px rgba(0,0,0,0.15) !important;
        }
        .swal-ro-title {
          font-size: 1.1rem !important;
          font-weight: 700 !important;
          color: #1e3a5f !important;
        }
        .swal-ro-confirm {
          border-radius: 0.6rem !important;
          font-weight: 600 !important;
          font-size: 0.85rem !important;
          padding: 0.55rem 1.4rem !important;
        }
        .swal-ro-cancel {
          border-radius: 0.6rem !important;
          font-weight: 600 !important;
          font-size: 0.85rem !important;
          padding: 0.55rem 1.4rem !important;
        }
      `}</style>

      <header className="bg-white border-b border-blue-100 sticky top-0 z-30 shadow-sm">
        <div className="w-full px-6 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-200">
              <RiFileTextLine className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-blue-800 tracking-tight">
                Release Order
              </h1>
              <p className="text-[11px] text-blue-400 tracking-widest uppercase">
                Create New RO
              </p>
            </div>
          </div>
          {loading && (
            <span className="flex items-center gap-2 text-xs text-blue-400">
              <Spinner /> Loading data…
            </span>
          )}
        </div>
      </header>

      <div className="px-6 py-6 pb-16 w-full mx-auto space-y-5">
        {/* Order Details */}
        <div className="bg-white border border-blue-100 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-blue-50 bg-blue-50/50">
            <span className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
              Order Details
            </span>
          </div>

          {previousRONumber && (
            <div className="px-5 py-2 bg-blue-50/40 border-b border-blue-100">
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <RiHistoryLine className="w-3.5 h-3.5 text-blue-500" />
                  <span className="text-slate-600">Previous RO:</span>
                  <span className="font-mono font-semibold text-slate-700">
                    {previousRONumber}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-600">Next RO:</span>
                  <span className="font-mono font-bold text-green-700">
                    {nextRONumber}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="p-5 grid grid-cols-3 gap-5">
            <div className="col-span-1 row-span-2">
              <Label>To</Label>
              <textarea
                value={form.to}
                onChange={(e) => setFormField("to", e.target.value)}
                rows={4}
                className={`${inputCls} resize-none`}
                placeholder="The Advertisement Manager"
              />
            </div>

            <div>
              <Label>Client</Label>
              <SelectWrapper>
                <select
                  value={form.client}
                  onChange={(e) => setFormField("client", e.target.value)}
                  className={selectCls}
                >
                  <option value="">--Select--</option>
                  {clients.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.bankName || c.name}
                    </option>
                  ))}
                </select>
              </SelectWrapper>
            </div>

            <div>
              <Label>Date</Label>
              <div className="relative">
                <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setFormField("date", e.target.value)}
                  className={`${inputCls} pl-9`}
                />
              </div>
            </div>

            <div>
              <Label>Staff / Agent</Label>
              <div className="flex gap-2 mb-2">
                {["staff", "agent"].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleSATypeChange(type)}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-semibold capitalize border transition
                      ${
                        form.staffOrAgent === type
                          ? "bg-blue-600 border-blue-600 text-white shadow-sm shadow-blue-200"
                          : "bg-blue-50 border-blue-200 text-blue-500 hover:bg-blue-100"
                      }`}
                  >
                    {type === "staff" ? (
                      <RiUserLine className="inline w-3.5 h-3.5 mr-1" />
                    ) : (
                      <RiTeamLine className="inline w-3.5 h-3.5 mr-1" />
                    )}
                    {type}
                  </button>
                ))}
              </div>
              <SelectWrapper>
                <select
                  value={form.staffAgentId}
                  onChange={(e) => setFormField("staffAgentId", e.target.value)}
                  className={selectCls}
                >
                  <option value="">--Select--</option>
                  {saList.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </SelectWrapper>
            </div>
          </div>

          <div className="px-5 pb-5">
            <Label>Caption</Label>
            <input
              type="text"
              value={form.caption}
              onChange={(e) => setFormField("caption", e.target.value)}
              className={inputCls}
              placeholder="Ad caption / description"
            />
          </div>
        </div>

        {/* Publication Items */}
        <div className="bg-white border border-blue-100 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-blue-50 bg-blue-50/50 flex items-center justify-between">
            <span className="text-xs font-semibold text-blue-700 uppercase tracking-widest flex items-center gap-2">
              <RiNewspaperLine className="w-4 h-4 text-blue-500" />
              Publication Items
            </span>
            <button
              onClick={addItem}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold transition shadow-sm shadow-blue-200"
            >
              <RiAddLine className="w-3.5 h-3.5" /> Add Item
            </button>
          </div>

          {form.items.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              <RiNewspaperLine className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No items added yet</p>
              <button
                onClick={addItem}
                className="mt-3 text-xs text-blue-600 hover:text-blue-700"
              >
                Click here to add first item
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-1 px-5 pt-4 overflow-x-auto pb-0 border-b border-blue-100">
                {form.items.map((item, idx) => {
                  const isActive = activeTab === idx;
                  const label = `Item ${idx + 1}`;
                  return (
                    <div
                      key={item.id}
                      className={`group relative flex items-center gap-1.5 px-4 py-2 rounded-t-xl text-xs font-semibold cursor-pointer border-b-2 transition-all whitespace-nowrap select-none
                        ${
                          isActive
                            ? "bg-white border-blue-600 text-blue-700 shadow-sm"
                            : "bg-blue-50/60 border-transparent text-blue-400 hover:text-blue-600 hover:bg-blue-50"
                        }`}
                      onClick={() => setActiveTab(idx)}
                    >
                      <RiNewspaperLine className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{label}</span>
                      {form.items.length > 1 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeItem(item.id);
                          }}
                          className={`ml-1 rounded-full p-0.5 transition
                            ${
                              isActive
                                ? "text-red-400 hover:text-red-600 hover:bg-red-50"
                                : "text-blue-300 hover:text-red-400 opacity-0 group-hover:opacity-100"
                            }`}
                        >
                          <RiCloseLine className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {activeItem &&
                (() => {
                  const itemWithCallback = {
                    ...activeItem,
                    _onChange: (key, val) => setItem(activeItem.id, key, val),
                  };
                  const sqcm =
                    Number(activeItem.width || 0) *
                    Number(activeItem.height || 0);

                  return (
                    <div className="p-5">
                      <div className="grid grid-cols-3 gap-5">
                        <div className="col-span-2 grid grid-cols-2 gap-4">
                          <div>
                            <Label>RO Number</Label>
                            <input
                              type="text"
                              value={activeItem.roNumber}
                              onChange={(e) =>
                                setItem(
                                  activeItem.id,
                                  "roNumber",
                                  e.target.value,
                                )
                              }
                              className={inputCls}
                              placeholder="e.g. NG/001(A)/25-26"
                            />
                            <div className="mt-1 text-[10px] text-blue-500">
                              Format: NG/001(A)/25-26 (Auto-generated, editable
                              if needed)
                            </div>
                          </div>

                          <div>
                            <Label>Media</Label>
                            <SelectWrapper>
                              <select
                                value={activeItem.media}
                                onChange={(e) =>
                                  handleMediaChange(
                                    activeItem.id,
                                    e.target.value,
                                  )
                                }
                                className={selectCls}
                              >
                                <option value="">--Select--</option>
                                {media.map((m) => (
                                  <option key={m._id} value={m._id}>
                                    {m.organisation}
                                  </option>
                                ))}
                              </select>
                            </SelectWrapper>
                          </div>

                          <div>
                            <Label>Publication</Label>
                            <input
                              value={activeItem.publication}
                              onChange={(e) =>
                                setItem(
                                  activeItem.id,
                                  "publication",
                                  e.target.value,
                                )
                              }
                              className={inputCls}
                              placeholder="Publication name"
                            />
                          </div>

                          <div>
                            <Label required>Edition</Label>
                            <input
                              value={activeItem.edition}
                              onChange={(e) =>
                                setItem(
                                  activeItem.id,
                                  "edition",
                                  e.target.value,
                                )
                              }
                              className={inputCls}
                              placeholder="Edition (required)"
                            />
                          </div>

                          <div>
                            <Label>HUE</Label>
                            <SelectWrapper>
                              <select
                                value={activeItem.hue}
                                onChange={(e) =>
                                  setItem(activeItem.id, "hue", e.target.value)
                                }
                                className={selectCls}
                              >
                                <option>B/W</option>
                                <option>Color</option>
                              </select>
                            </SelectWrapper>
                          </div>

                          <div>
                            <Label>Width (cm)</Label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={activeItem.width}
                              onChange={(e) =>
                                setItem(activeItem.id, "width", e.target.value)
                              }
                              className={inputCls}
                              placeholder="0"
                            />
                          </div>

                          <div>
                            <Label>Height (cm)</Label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={activeItem.height}
                              onChange={(e) =>
                                setItem(activeItem.id, "height", e.target.value)
                              }
                              className={inputCls}
                              placeholder="0"
                            />
                          </div>

                          <div>
                            <Label>Date of Publication</Label>
                            <div className="relative">
                              <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                              <input
                                type="date"
                                value={activeItem.dop}
                                onChange={(e) =>
                                  setItem(activeItem.id, "dop", e.target.value)
                                }
                                className={`${inputCls} pl-9`}
                              />
                            </div>
                          </div>

                          <div>
                            <Label>Page / Position</Label>
                            <input
                              value={activeItem.pagePosition}
                              onChange={(e) =>
                                setItem(
                                  activeItem.id,
                                  "pagePosition",
                                  e.target.value,
                                )
                              }
                              className={inputCls}
                              placeholder="Page / Position"
                            />
                          </div>

                          <div>
                            <Label>Ad Type</Label>
                            <SelectWrapper>
                              <select
                                value={activeItem.adType}
                                onChange={(e) =>
                                  handleAdTypeChange(
                                    activeItem.id,
                                    e.target.value,
                                  )
                                }
                                className={selectCls}
                                disabled={!activeItem.media}
                              >
                                <option value="">--Select Ad Type--</option>
                                {(adTypes[activeItem.media] || []).map(
                                  (adType) => (
                                    <option key={adType._id} value={adType._id}>
                                      Type {adType.typeNumber} - ₹{adType.price}
                                      /sq cm ({adType.names?.length || 0} names)
                                    </option>
                                  ),
                                )}
                              </select>
                            </SelectWrapper>

                            {activeItem.adType && adTypes[activeItem.media] && (
                              <div className="mt-3">
                                <div className="text-[10px] font-semibold text-blue-600 uppercase tracking-widest mb-2 flex items-center gap-2">
                                  <RiListCheck2 className="w-3.5 h-3.5" />
                                  Available Ad Names for Type{" "}
                                  {
                                    adTypes[activeItem.media].find(
                                      (at) => at._id === activeItem.adType,
                                    )?.typeNumber
                                  }
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {adTypes[activeItem.media]
                                    .find((at) => at._id === activeItem.adType)
                                    ?.names?.map((name, idx) => {
                                      const selectedAdType = adTypes[
                                        activeItem.media
                                      ].find(
                                        (at) => at._id === activeItem.adType,
                                      );
                                      return (
                                        <button
                                          key={idx}
                                          type="button"
                                          onClick={() =>
                                            handleAdNameSelect(
                                              activeItem.id,
                                              name,
                                              selectedAdType,
                                            )
                                          }
                                          className={`text-xs px-3 py-1.5 rounded-lg transition ${
                                            activeItem.selectedAdName === name
                                              ? "bg-blue-600 text-white border border-blue-600 shadow-sm"
                                              : "bg-white border border-blue-200 text-slate-700 hover:bg-blue-50"
                                          }`}
                                        >
                                          {name}
                                        </button>
                                      );
                                    })}
                                </div>
                                {activeItem.selectedAdName && (
                                  <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-lg">
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs font-semibold text-green-700">
                                        Selected: {activeItem.selectedAdName}
                                      </span>
                                      <span className="text-sm font-bold text-green-700">
                                        Rate: ₹{activeItem.rate}/sq cm
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {adTypes[activeItem.media]?.length === 0 &&
                              activeItem.media && (
                                <p className="text-[10px] text-amber-500 mt-1">
                                  No ad types available for this media
                                </p>
                              )}
                          </div>

                          <div>
                            <Label>Rate (Rs/Sq.cm)</Label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={activeItem.rate}
                              onChange={(e) =>
                                setItem(activeItem.id, "rate", e.target.value)
                              }
                              className={inputCls}
                              placeholder="0"
                            />
                          </div>

                          <div className="col-span-2">
                            <div className="flex items-center gap-4 px-4 py-2.5 bg-blue-50/60 border border-blue-100 rounded-xl">
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-bold text-blue-500 uppercase tracking-widest">
                                  Area
                                </span>
                                <span className="font-mono font-bold text-slate-700 text-sm">
                                  {sqcm.toFixed(2)} sq.cm
                                </span>
                              </div>
                              <div className="w-px h-5 bg-blue-200" />
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-bold text-blue-500 uppercase tracking-widest">
                                  Line Gross
                                </span>
                                <span className="font-mono font-bold text-blue-700 text-sm">
                                  ₹{fmt(sqcm * Number(activeItem.rate || 0))}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Per-Item Cheque Details */}
                          <div className="col-span-2 border-t border-blue-100 pt-4 mt-2">
                            <div className="flex items-center gap-2 mb-3">
                              <RiBankLine className="w-4 h-4 text-blue-500" />
                              <Label className="!mb-0">Cheque Details</Label>
                              <span className="text-[10px] text-blue-400 ml-2">
                                Item {activeTab + 1}
                              </span>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                              <div>
                                <Label>Bank Name</Label>
                                <div className="relative">
                                  <RiBankLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                                  <input
                                    type="text"
                                    value={
                                      activeItem.chequeDetails?.bankName || ""
                                    }
                                    onChange={(e) =>
                                      setItemChequeDetail(
                                        activeItem.id,
                                        "bankName",
                                        e.target.value,
                                      )
                                    }
                                    className={`${inputCls} pl-9`}
                                    placeholder="e.g., HDFC Bank"
                                  />
                                </div>
                              </div>
                              <div>
                                <Label>Cheque No.</Label>
                                <div className="relative">
                                  <RiWalletLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                                  <input
                                    type="text"
                                    value={
                                      activeItem.chequeDetails?.chequeNo || ""
                                    }
                                    onChange={(e) =>
                                      setItemChequeDetail(
                                        activeItem.id,
                                        "chequeNo",
                                        e.target.value,
                                      )
                                    }
                                    className={`${inputCls} pl-9`}
                                    placeholder="e.g., 123456"
                                  />
                                </div>
                              </div>
                              <div>
                                <Label>Cheque Date</Label>
                                <div className="relative">
                                  <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                                  <input
                                    type="date"
                                    value={
                                      activeItem.chequeDetails?.chequeDate || ""
                                    }
                                    onChange={(e) =>
                                      setItemChequeDetail(
                                        activeItem.id,
                                        "chequeDate",
                                        e.target.value,
                                      )
                                    }
                                    className={`${inputCls} pl-9`}
                                  />
                                </div>
                              </div>
                            </div>
                            <p className="text-[10px] text-blue-400 mt-2 italic">
                              These fields are optional and can be left blank
                            </p>
                          </div>
                        </div>

                        <div className="col-span-1">
                          <ItemFinancials item={itemWithCallback} />
                        </div>
                      </div>
                    </div>
                  );
                })()}
            </>
          )}
        </div>

        {/* Grand Total */}
        {form.items.length > 1 && (
          <div className="flex justify-end">
            <div className="bg-white border border-blue-100 rounded-2xl shadow-sm overflow-hidden w-full max-w-md">
              <div className="px-5 py-3 border-b border-blue-50 bg-blue-50/50">
                <span className="text-xs font-semibold text-blue-700 uppercase tracking-widest flex items-center gap-2">
                  <RiCalculatorLine className="w-4 h-4 text-blue-500" />
                  Grand Total — All Items
                </span>
              </div>
              <div className="p-5 space-y-2 text-sm">
                <div className="flex justify-between py-1.5 border-b border-blue-50">
                  <span className="text-slate-600 font-medium">
                    Total Gross
                  </span>
                  <span className="font-bold text-slate-800 font-mono">
                    ₹{fmt(totals.gross)}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Agency Commission</span>
                  <span className="text-red-500 font-mono">
                    − ₹{fmt(totals.agencyComm)}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Other Discount</span>
                  <span className="text-red-500 font-mono">
                    − ₹{fmt(totals.otherDiscount)}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Other Premium</span>
                  <span className="text-green-500 font-mono">
                    + ₹{fmt(totals.otherPremium)}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Cash Discount</span>
                  <span className="text-red-500 font-mono">
                    − ₹{fmt(totals.cashDisc)}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>GST</span>
                  <span className="text-emerald-600 font-mono">
                    + ₹{fmt(totals.gstAmt)}
                  </span>
                </div>
                <div className="flex items-center justify-between pt-3 border-t-2 border-blue-600/20 mt-1">
                  <span className="text-blue-800 font-bold text-sm">
                    Net Payable Amount
                  </span>
                  <span className="font-extrabold text-blue-700 text-lg font-mono">
                    ₹{fmt(totals.net)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            onClick={resetForm}
            className="flex items-center gap-1.5 px-6 py-2.5 rounded-xl text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 font-semibold transition"
          >
            <RiCloseLine className="w-4 h-4" /> Reset
          </button>

          {lastCreatedRO && (
            <button
              onClick={() =>
                generateReleaseOrderPDF(lastCreatedRO, RO_IMAGE_BASE64)
              }
              className="flex items-center gap-2 px-7 py-2.5 rounded-xl text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white transition shadow-lg shadow-emerald-200"
            >
              <RiDownloadLine className="w-4 h-4" />
              Download PDF
            </button>
          )}

          <button
            onClick={handleSubmit}
            disabled={submitting || form.items.length === 0}
            className="flex items-center gap-2 px-7 py-2.5 rounded-xl text-sm font-bold bg-blue-600 hover:bg-blue-700 text-white transition shadow-lg shadow-blue-200 disabled:opacity-60"
          >
            {submitting ? <Spinner /> : <RiCheckLine className="w-4 h-4" />}
            {submitting ? "Creating…" : "Create Release Order"}
          </button>
        </div>
      </div>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}

// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import {
//   RiAddLine,
//   RiCloseLine,
//   RiCheckLine,
//   RiErrorWarningLine,
//   RiCalendarLine,
//   RiFileTextLine,
//   RiUserLine,
//   RiTeamLine,
//   RiNewspaperLine,
//   RiCalculatorLine,
//   RiListCheck2,
//   RiArrowDownSLine,
//   RiHistoryLine,
//   RiBankLine,
//   RiWalletLine,
// } from "react-icons/ri";
// import { AiOutlineLoading3Quarters } from "react-icons/ai";
// import {
//   generateReleaseOrderPDF,
//   RO_IMAGE_BASE64,
// } from "../Util/GenerateReleaseOrderPDF";
// import { RiDownloadLine } from "react-icons/ri";

// const BASE_URL = process.env.REACT_APP_API_URL;

// const Spinner = ({ className = "w-4 h-4" }) => (
//   <AiOutlineLoading3Quarters className={`animate-spin ${className}`} />
// );

// const Toast = ({ message, type, onClose }) => {
//   useEffect(() => {
//     const t = setTimeout(onClose, 3500);
//     return () => clearTimeout(t);
//   }, [onClose]);
//   return (
//     <div
//       className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium
//       ${
//         type === "success"
//           ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
//           : "bg-red-50 border border-red-200 text-red-800"
//       }`}
//     >
//       {type === "success" ? (
//         <RiCheckLine className="w-4 h-4 text-emerald-500" />
//       ) : (
//         <RiErrorWarningLine className="w-4 h-4 text-red-500" />
//       )}
//       <span>{message}</span>
//       <button
//         onClick={onClose}
//         className="ml-2 opacity-50 hover:opacity-100 transition"
//       >
//         <RiCloseLine className="w-4 h-4" />
//       </button>
//     </div>
//   );
// };

// const tomorrow = () => {
//   const d = new Date();
//   d.setDate(d.getDate() + 1);
//   return d.toISOString().split("T")[0];
// };

// const fmt = (n) =>
//   Number(n || 0).toLocaleString("en-IN", {
//     minimumFractionDigits: 2,
//     maximumFractionDigits: 2,
//   });

// const emptyItem = (nextRONumber = "") => ({
//   id: Math.random().toString(36).slice(2),
//   media: "",
//   publication: "",
//   edition: "",
//   hue: "B/W",
//   width: "",
//   height: "",
//   dop: tomorrow(),
//   pagePosition: "",
//   adType: "",
//   selectedAdName: "",
//   rate: "",
//   roNumber: nextRONumber,
//   agencyCommission: 15,
//   otherDiscount: 0,
//   otherPremium: 0,
//   discountType: "discount",
//   cashDiscount: 0,
//   gst: 5,
//   chequeDetails: {
//     bankName: "",
//     chequeNo: "",
//     chequeDate: "",
//   },
// });

// const defaultForm = {
//   to: "The Advertisement Manager",
//   client: "",
//   caption: "",
//   staffOrAgent: "staff",
//   staffAgentId: "",
//   date: tomorrow(),
//   items: [],
// };

// function calcItemAmounts(item) {
//   const sqcm = Number(item.width || 0) * Number(item.height || 0);
//   const gross = sqcm * Number(item.rate || 0);
//   const agencyComm = (gross * Number(item.agencyCommission || 0)) / 100;

//   let otherAmount = 0;
//   if (item.discountType === "discount") {
//     otherAmount = -Number(item.otherDiscount || 0);
//   } else {
//     otherAmount = Number(item.otherPremium || 0);
//   }

//   const afterComm = gross - agencyComm + otherAmount;
//   const cashDisc = (afterComm * Number(item.cashDiscount || 0)) / 100;
//   const taxable = afterComm - cashDisc;
//   const gstAmt = (taxable * Number(item.gst || 0)) / 100;
//   const net = taxable + gstAmt;

//   return {
//     sqcm,
//     gross,
//     agencyComm,
//     otherDiscount: item.discountType === "discount" ? item.otherDiscount : 0,
//     otherPremium: item.discountType === "premium" ? item.otherPremium : 0,
//     cashDisc,
//     gstAmt,
//     net,
//   };
// }

// function calcTotals(items) {
//   return items.reduce(
//     (acc, item) => {
//       const a = calcItemAmounts(item);
//       acc.gross += a.gross;
//       acc.agencyComm += a.agencyComm;
//       acc.otherDiscount += a.otherDiscount;
//       acc.otherPremium += a.otherPremium;
//       acc.cashDisc += a.cashDisc;
//       acc.gstAmt += a.gstAmt;
//       acc.net += a.net;
//       return acc;
//     },
//     {
//       gross: 0,
//       agencyComm: 0,
//       otherDiscount: 0,
//       otherPremium: 0,
//       cashDisc: 0,
//       gstAmt: 0,
//       net: 0,
//     },
//   );
// }

// const Label = ({ children, className = "", required = false }) => (
//   <label
//     className={`text-[10px] font-semibold text-blue-600 uppercase tracking-widest mb-1 block ${className}`}
//   >
//     {children}
//     {required && <span className="text-red-500 ml-1">*</span>}
//   </label>
// );

// const inputCls =
//   "w-full bg-blue-50/60 border border-blue-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition";

// const selectCls =
//   "w-full bg-[#3d2626] border border-[#3d2626] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition appearance-none cursor-pointer";

// const SelectWrapper = ({ children }) => (
//   <div className="relative">
//     {children}
//     <RiArrowDownSLine className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white pointer-events-none" />
//   </div>
// );

// function ItemFinancials({ item }) {
//   const amounts = calcItemAmounts(item);

//   return (
//     <div className="bg-blue-50/40 border border-blue-100 rounded-xl p-4 space-y-2.5 text-sm">
//       <div className="text-[10px] font-bold text-blue-500 uppercase tracking-widest mb-3">
//         Financial Summary
//       </div>

//       <div className="flex items-center justify-between py-1.5 border-b border-blue-100">
//         <span className="text-slate-500 text-xs">Gross Amount</span>
//         <span className="font-bold text-slate-800 font-mono text-sm">
//           ₹{fmt(amounts.gross)}
//         </span>
//       </div>

//       <div className="flex items-center gap-2">
//         <span className="text-slate-500 text-xs flex-1">Agency Commission</span>
//         <div className="flex items-center gap-1.5">
//           <input
//             type="number"
//             min="0"
//             max="100"
//             value={item.agencyCommission}
//             onChange={(e) => item._onChange("agencyCommission", e.target.value)}
//             className="w-12 text-center border border-blue-200 rounded-lg py-1 text-xs bg-white focus:outline-none focus:border-blue-500 transition"
//           />
//           <span className="text-[10px] text-blue-400">%</span>
//           <span className="font-semibold text-red-500 font-mono text-xs w-20 text-right">
//             − ₹{fmt(amounts.agencyComm)}
//           </span>
//         </div>
//       </div>

//       <div className="flex items-center gap-2">
//         <span className="text-slate-500 text-xs flex-1">Other</span>
//         <div className="flex items-center gap-1.5 flex-wrap">
//           <div className="relative">
//             <select
//               value={item.discountType}
//               onChange={(e) => item._onChange("discountType", e.target.value)}
//               className="border border-blue-200 rounded-lg px-2 py-1 text-xs bg-white text-slate-700 focus:outline-none focus:border-blue-500 appearance-none pr-5 cursor-pointer"
//             >
//               <option value="discount">Discount</option>
//               <option value="premium">Premium</option>
//             </select>
//             <RiArrowDownSLine className="absolute right-0.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
//           </div>
//           <input
//             type="number"
//             min="0"
//             value={
//               item.discountType === "discount"
//                 ? item.otherDiscount
//                 : item.otherPremium
//             }
//             onChange={(e) => {
//               if (item.discountType === "discount") {
//                 item._onChange("otherDiscount", e.target.value);
//               } else {
//                 item._onChange("otherPremium", e.target.value);
//               }
//             }}
//             className="w-16 text-center border border-blue-200 rounded-lg py-1 text-xs bg-white focus:outline-none focus:border-blue-500 transition"
//           />
//           <span className="text-[10px] text-blue-400">Rs.</span>
//           <span
//             className={`font-semibold font-mono text-xs w-20 text-right ${
//               item.discountType === "discount"
//                 ? "text-red-500"
//                 : "text-green-500"
//             }`}
//           >
//             {item.discountType === "discount" ? "−" : "+"} ₹
//             {fmt(
//               item.discountType === "discount"
//                 ? amounts.otherDiscount
//                 : amounts.otherPremium,
//             )}
//           </span>
//         </div>
//       </div>

//       <div className="flex items-center gap-2">
//         <span className="text-slate-500 text-xs flex-1">Cash Discount</span>
//         <div className="flex items-center gap-1.5">
//           <input
//             type="number"
//             min="0"
//             max="100"
//             value={item.cashDiscount}
//             onChange={(e) => item._onChange("cashDiscount", e.target.value)}
//             className="w-12 text-center border border-blue-200 rounded-lg py-1 text-xs bg-white focus:outline-none focus:border-blue-500 transition"
//           />
//           <span className="text-[10px] text-blue-400">%</span>
//           <span className="font-semibold text-red-500 font-mono text-xs w-20 text-right">
//             − ₹{fmt(amounts.cashDisc)}
//           </span>
//         </div>
//       </div>

//       <div className="flex items-center gap-2">
//         <span className="text-slate-500 text-xs flex-1">GST</span>
//         <div className="flex items-center gap-1.5">
//           <input
//             type="number"
//             min="0"
//             max="100"
//             value={item.gst}
//             onChange={(e) => item._onChange("gst", e.target.value)}
//             className="w-12 text-center border border-blue-200 rounded-lg py-1 text-xs bg-white focus:outline-none focus:border-blue-500 transition"
//           />
//           <span className="text-[10px] text-blue-400">%</span>
//           <span className="font-semibold text-emerald-600 font-mono text-xs w-20 text-right">
//             + ₹{fmt(amounts.gstAmt)}
//           </span>
//         </div>
//       </div>

//       <div className="flex items-center justify-between pt-2.5 border-t-2 border-blue-600/20 mt-1">
//         <span className="text-blue-800 font-bold text-xs">Net Payable</span>
//         <span className="font-extrabold text-blue-700 text-base font-mono">
//           ₹{fmt(amounts.net)}
//         </span>
//       </div>
//     </div>
//   );
// }

// export default function CreateReleaseOrder() {
//   const [form, setForm] = useState(defaultForm);
//   const [activeTab, setActiveTab] = useState(0);
//   const [clients, setClients] = useState([]);
//   const [media, setMedia] = useState([]);
//   const [staff, setStaff] = useState([]);
//   const [agents, setAgents] = useState([]);
//   const [nextRONumber, setNextRONumber] = useState("");
//   const [previousRONumber, setPreviousRONumber] = useState("");
//   const [loading, setLoading] = useState(false);
//   const [submitting, setSubmitting] = useState(false);
//   const [toast, setToast] = useState(null);
//   const [lastCreatedRO, setLastCreatedRO] = useState(null);
//   const [adTypes, setAdTypes] = useState({});

//   const notify = (message, type = "success") => setToast({ message, type });

//   // Generate next RO number from base RO number
//   const generateNextRONumber = (baseRO) => {
//     if (!baseRO) return "";

//     // Pattern: NG/001(A)/25-26
//     const matchWithSuffix = baseRO.match(
//       /^(.*\/)(\d+)\(([A-Z])\)(\/\d{2}-\d{2})$/,
//     );
//     if (matchWithSuffix) {
//       const prefix = matchWithSuffix[1];
//       const num = matchWithSuffix[2];
//       const currentLetter = matchWithSuffix[3];
//       const year = matchWithSuffix[4];
//       const nextLetter = String.fromCharCode(currentLetter.charCodeAt(0) + 1);
//       return `${prefix}${num}(${nextLetter})${year}`;
//     }

//     // Pattern: NG/001/25-26 (without suffix)
//     const matchWithoutSuffix = baseRO.match(/^(.*\/)(\d+)(\/\d{2}-\d{2})$/);
//     if (matchWithoutSuffix) {
//       const prefix = matchWithoutSuffix[1];
//       const num = matchWithoutSuffix[2];
//       const year = matchWithoutSuffix[3];
//       // First item gets (A) suffix
//       return `${prefix}${num}(A)${year}`;
//     }

//     return "";
//   };

//   // Format RO number with suffix
//   const formatRONumberWithSuffix = (roNumber, suffix = "A") => {
//     if (!roNumber) return "";

//     const matchWithoutSuffix = roNumber.match(/^(.*\/)(\d+)(\/\d{2}-\d{2})$/);
//     if (matchWithoutSuffix) {
//       const prefix = matchWithoutSuffix[1];
//       const num = matchWithoutSuffix[2];
//       const year = matchWithoutSuffix[3];
//       return `${prefix}${num}(${suffix})${year}`;
//     }

//     return roNumber;
//   };

//   useEffect(() => {
//     const fetchAll = async () => {
//       setLoading(true);
//       try {
//         const [clientsRes, mediaRes, staffRes, agentsRes, previousRORes] =
//           await Promise.allSettled([
//             axios.get(`${BASE_URL}/bank/get-banks`),
//             axios.get(`${BASE_URL}/media/get-all`),
//             axios.get(`${BASE_URL}/admin/all-users`),
//             axios.get(`${BASE_URL}/agent/all-agents`),
//             axios.get(`${BASE_URL}/releaseOrder/ro-numbers/previous`),
//           ]);

//         if (clientsRes.status === "fulfilled")
//           setClients(
//             clientsRes?.value?.data?.data || clientsRes.value.data || [],
//           );
//         if (mediaRes.status === "fulfilled")
//           setMedia(mediaRes?.value?.data?.data || mediaRes.value.data || []);
//         if (staffRes.status === "fulfilled") {
//           const all = staffRes?.value?.data?.data || staffRes.value.data || [];
//           setStaff(all.filter((u) => u.userType === "staff"));
//         }
//         if (agentsRes.status === "fulfilled")
//           setAgents(agentsRes?.value?.data?.data || agentsRes.value.data || []);
//         if (previousRORes.status === "fulfilled") {
//           const roData = previousRORes.value?.data?.data;
//           if (roData) {
//             setPreviousRONumber(roData.previous || "None");
//             setNextRONumber(roData.next);

//             if (roData.next) {
//               // Format the first item's RO number with (A) suffix
//               const firstItemRONumber = formatRONumberWithSuffix(
//                 roData.next,
//                 "A",
//               );
//               const firstItem = emptyItem(firstItemRONumber);
//               setForm((f) => ({ ...f, items: [firstItem] }));
//             }
//           }
//         }
//       } catch (error) {
//         console.error("Fetch error:", error);
//         notify("Some data failed to load", "error");
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchAll();
//   }, []);

//   const fetchAdTypesForMedia = async (mediaId) => {
//     if (!mediaId) return;
//     try {
//       const res = await axios.get(`${BASE_URL}/media/adtypes/${mediaId}`);
//       if (res.data?.success && res.data?.adTypes) {
//         setAdTypes((prev) => ({
//           ...prev,
//           [mediaId]: res.data.adTypes,
//         }));
//       }
//     } catch (error) {
//       console.error("Failed to fetch ad types:", error);
//     }
//   };

//   const setFormField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

//   const setItemChequeDetail = (itemId, key, val) =>
//     setForm((f) => ({
//       ...f,
//       items: f.items.map((it) =>
//         it.id === itemId
//           ? {
//               ...it,
//               chequeDetails: { ...it.chequeDetails, [key]: val },
//             }
//           : it,
//       ),
//     }));

//   const setItem = (id, key, val) =>
//     setForm((f) => ({
//       ...f,
//       items: f.items.map((it) => (it.id === id ? { ...it, [key]: val } : it)),
//     }));

//   const addItem = () => {
//     if (form.items.length >= 26) {
//       notify("Maximum 26 items allowed", "error");
//       return;
//     }

//     let newRONumber = "";

//     if (form.items.length === 0) {
//       // First item - use the base nextRONumber with (A) suffix
//       if (nextRONumber) {
//         newRONumber = formatRONumberWithSuffix(nextRONumber, "A");
//       }
//     } else if (form.items.length > 0) {
//       // Subsequent items - generate from the last item
//       const lastItem = form.items[form.items.length - 1];
//       if (lastItem.roNumber) {
//         newRONumber = generateNextRONumber(lastItem.roNumber);
//       }
//     }

//     if (!newRONumber && nextRONumber) {
//       newRONumber = formatRONumberWithSuffix(nextRONumber, "A");
//     }

//     const newItem = emptyItem(newRONumber);
//     setForm((f) => ({ ...f, items: [...f.items, newItem] }));
//     setActiveTab(form.items.length);
//   };

//   const removeItem = (id) => {
//     const idx = form.items.findIndex((it) => it.id === id);
//     setForm((f) => ({ ...f, items: f.items.filter((it) => it.id !== id) }));
//     setActiveTab(Math.max(0, idx - 1));
//   };

//   const handleMediaChange = async (itemId, mediaId) => {
//     const found = media.find((m) => m._id === mediaId);
//     setItem(itemId, "media", mediaId);
//     setItem(itemId, "adType", "");
//     setItem(itemId, "selectedAdName", "");
//     await fetchAdTypesForMedia(mediaId);
//     if (found?.pricePerSqCm) {
//       setItem(itemId, "rate", found.pricePerSqCm);
//     }
//     if (found?.organisation) {
//       setItem(itemId, "publication", found.organisation);
//     }
//   };

//   const handleAdTypeChange = (itemId, selectedAdTypeId) => {
//     const currentItem = form.items.find((item) => item.id === itemId);
//     const mediaId = currentItem?.media;
//     const selectedAdType = adTypes[mediaId]?.find(
//       (at) => at._id === selectedAdTypeId,
//     );

//     setItem(itemId, "adType", selectedAdTypeId);
//     setItem(itemId, "selectedAdName", "");

//     if (selectedAdType?.price) {
//       setItem(itemId, "rate", selectedAdType.price);
//     }
//   };

//   const handleAdNameSelect = (itemId, adName, selectedAdType) => {
//     setItem(itemId, "selectedAdName", adName);
//     if (selectedAdType?.price) {
//       setItem(itemId, "rate", selectedAdType.price);
//     }
//   };

//   const handleSATypeChange = (val) => {
//     setForm((f) => ({ ...f, staffOrAgent: val, staffAgentId: "" }));
//   };

//   const handleSubmit = async () => {
//     if (!form.client) return notify("Please select a client", "error");
//     if (!form.staffAgentId)
//       return notify("Please select a staff/agent", "error");
//     if (form.items.length === 0)
//       return notify("Please add at least one item", "error");

//     for (let i = 0; i < form.items.length; i++) {
//       const item = form.items[i];

//       if (!item.edition) {
//         notify(`Please enter edition for Item ${i + 1}`, "error");
//         setActiveTab(i);
//         return;
//       }

//       if (!item.selectedAdName) {
//         notify(`Please select an ad name for Item ${i + 1}`, "error");
//         setActiveTab(i);
//         return;
//       }

//       if (!item.roNumber) {
//         notify(`RO number is required for Item ${i + 1}`, "error");
//         setActiveTab(i);
//         return;
//       }

//       const roMatch = item.roNumber.match(
//         /^(.*\/)(\d+)(\([A-Z]\))?(\/\d{2}-\d{2})$/,
//       );
//       if (!roMatch) {
//         notify(
//           `Item ${i + 1}: RO number must follow format: NG/1209/25-26 or NG/1209(A)/25-26`,
//           "error",
//         );
//         setActiveTab(i);
//         return;
//       }

//       if (!item.width || item.width <= 0) {
//         notify(`Please enter valid width for Item ${i + 1}`, "error");
//         setActiveTab(i);
//         return;
//       }

//       if (!item.height || item.height <= 0) {
//         notify(`Please enter valid height for Item ${i + 1}`, "error");
//         setActiveTab(i);
//         return;
//       }

//       if (!item.rate || item.rate <= 0) {
//         notify(`Please enter valid rate for Item ${i + 1}`, "error");
//         setActiveTab(i);
//         return;
//       }
//     }

//     setSubmitting(true);
//     try {
//       const clientObj = clients.find((c) => c._id === form.client);
//       const saList = form.staffOrAgent === "staff" ? staff : agents;
//       const saObj = saList.find((s) => s._id === form.staffAgentId);

//       const itemsForPDF = form.items.map(
//         ({ id, _onChange, discountType, chequeDetails, ...rest }) => ({
//           ...rest,
//           width: parseFloat(rest.width) || 0,
//           height: parseFloat(rest.height) || 0,
//           rate: parseFloat(rest.rate) || 0,
//           agencyCommission: parseFloat(rest.agencyCommission) || 0,
//           otherDiscount:
//             discountType === "discount"
//               ? parseFloat(rest.otherDiscount) || 0
//               : 0,
//           otherPremium:
//             discountType === "premium" ? parseFloat(rest.otherPremium) || 0 : 0,
//           cashDiscount: parseFloat(rest.cashDiscount) || 0,
//           gst: parseFloat(rest.gst) || 0,
//           adType: rest.selectedAdName || rest.adType || "",
//           chequeDetails: {
//             bankName: chequeDetails?.bankName || "",
//             chequeNo: chequeDetails?.chequeNo || "",
//             chequeDate: chequeDetails?.chequeDate || "",
//           },
//         }),
//       );

//       const payload = {
//         to: form.to,
//         client: form.client,
//         caption: form.caption,
//         date: form.date,
//         items: itemsForPDF,
//       };

//       if (form.staffOrAgent === "staff") payload.staff = form.staffAgentId;
//       else payload.agent = form.staffAgentId;

//       await axios.post(`${BASE_URL}/releaseOrder/create`, payload);

//       setLastCreatedRO({
//         roNumber: itemsForPDF[0]?.roNumber || nextRONumber,
//         date: form.date,
//         createdAt: new Date().toISOString(),
//         caption: form.caption,
//         client: clientObj || { bankName: form.client },
//         staff: form.staffOrAgent === "staff" ? saObj : undefined,
//         agent: form.staffOrAgent === "agent" ? saObj : undefined,
//         items: itemsForPDF,
//       });

//       notify("Release Order created successfully!");

//       const refreshRORes = await axios.get(
//         `${BASE_URL}/releaseOrder/ro-numbers/previous`,
//       );
//       if (refreshRORes.data?.success) {
//         const roData = refreshRORes.data.data;
//         setPreviousRONumber(roData.previous || "None");
//         setNextRONumber(roData.next);

//         // Format the first item's RO number with (A) suffix
//         const firstItemRONumber = formatRONumberWithSuffix(roData.next, "A");
//         const newItem = emptyItem(firstItemRONumber);
//         setForm({
//           to: "The Advertisement Manager",
//           client: "",
//           caption: "",
//           staffOrAgent: "staff",
//           staffAgentId: "",
//           date: tomorrow(),
//           items: [newItem],
//         });
//         setActiveTab(0);
//       }
//     } catch (error) {
//       console.error("Submit error:", error);
//       notify(
//         error.response?.data?.message || "Failed to create Release Order",
//         "error",
//       );
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   const resetForm = () => {
//     if (nextRONumber) {
//       // Format the first item's RO number with (A) suffix
//       const firstItemRONumber = formatRONumberWithSuffix(nextRONumber, "A");
//       const newItem = emptyItem(firstItemRONumber);
//       setForm({
//         to: "The Advertisement Manager",
//         client: "",
//         caption: "",
//         staffOrAgent: "staff",
//         staffAgentId: "",
//         date: tomorrow(),
//         items: [newItem],
//       });
//       setActiveTab(0);
//       setLastCreatedRO(null);
//     }
//   };

//   const saList = form.staffOrAgent === "staff" ? staff : agents;
//   const totals = calcTotals(form.items);
//   const activeItem = form.items[activeTab];

//   return (
//     <div className="min-h-screen bg-slate-50 text-slate-800">
//       <header className="bg-white border-b border-blue-100 sticky top-0 z-30 shadow-sm">
//         <div className="w-full px-6 py-3.5 flex items-center justify-between gap-4">
//           <div className="flex items-center gap-3">
//             <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-200">
//               <RiFileTextLine className="w-5 h-5" />
//             </div>
//             <div>
//               <h1 className="text-base font-bold text-blue-800 tracking-tight">
//                 Release Order
//               </h1>
//               <p className="text-[11px] text-blue-400 tracking-widest uppercase">
//                 Create New RO
//               </p>
//             </div>
//           </div>
//           {loading && (
//             <span className="flex items-center gap-2 text-xs text-blue-400">
//               <Spinner /> Loading data…
//             </span>
//           )}
//         </div>
//       </header>

//       <div className="px-6 py-6 pb-16 w-full mx-auto space-y-5">
//         {/* Order Details */}
//         <div className="bg-white border border-blue-100 rounded-2xl shadow-sm overflow-hidden">
//           <div className="px-5 py-3 border-b border-blue-50 bg-blue-50/50">
//             <span className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
//               Order Details
//             </span>
//           </div>

//           {previousRONumber && (
//             <div className="px-5 py-2 bg-blue-50/40 border-b border-blue-100">
//               <div className="flex items-center justify-between text-xs">
//                 <div className="flex items-center gap-2">
//                   <RiHistoryLine className="w-3.5 h-3.5 text-blue-500" />
//                   <span className="text-slate-600">Previous RO:</span>
//                   <span className="font-mono font-semibold text-slate-700">
//                     {previousRONumber}
//                   </span>
//                 </div>
//                 <div className="flex items-center gap-2">
//                   <span className="text-green-600">Next RO:</span>
//                   <span className="font-mono font-bold text-green-700">
//                     {nextRONumber}
//                   </span>
//                 </div>
//               </div>
//             </div>
//           )}

//           <div className="p-5 grid grid-cols-3 gap-5">
//             <div className="col-span-1 row-span-2">
//               <Label>To</Label>
//               <textarea
//                 value={form.to}
//                 onChange={(e) => setFormField("to", e.target.value)}
//                 rows={4}
//                 className={`${inputCls} resize-none`}
//                 placeholder="The Advertisement Manager"
//               />
//             </div>

//             <div>
//               <Label>Client</Label>
//               <SelectWrapper>
//                 <select
//                   value={form.client}
//                   onChange={(e) => setFormField("client", e.target.value)}
//                   className={selectCls}
//                 >
//                   <option value="">--Select--</option>
//                   {clients.map((c) => (
//                     <option key={c._id} value={c._id}>
//                       {c.bankName || c.name}
//                     </option>
//                   ))}
//                 </select>
//               </SelectWrapper>
//             </div>

//             <div>
//               <Label>Date</Label>
//               <div className="relative">
//                 <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
//                 <input
//                   type="date"
//                   value={form.date}
//                   onChange={(e) => setFormField("date", e.target.value)}
//                   className={`${inputCls} pl-9`}
//                 />
//               </div>
//             </div>

//             <div>
//               <Label>Staff / Agent</Label>
//               <div className="flex gap-2 mb-2">
//                 {["staff", "agent"].map((type) => (
//                   <button
//                     key={type}
//                     type="button"
//                     onClick={() => handleSATypeChange(type)}
//                     className={`flex-1 py-1.5 rounded-lg text-xs font-semibold capitalize border transition
//                       ${
//                         form.staffOrAgent === type
//                           ? "bg-blue-600 border-blue-600 text-white shadow-sm shadow-blue-200"
//                           : "bg-blue-50 border-blue-200 text-blue-500 hover:bg-blue-100"
//                       }`}
//                   >
//                     {type === "staff" ? (
//                       <RiUserLine className="inline w-3.5 h-3.5 mr-1" />
//                     ) : (
//                       <RiTeamLine className="inline w-3.5 h-3.5 mr-1" />
//                     )}
//                     {type}
//                   </button>
//                 ))}
//               </div>
//               <SelectWrapper>
//                 <select
//                   value={form.staffAgentId}
//                   onChange={(e) => setFormField("staffAgentId", e.target.value)}
//                   className={selectCls}
//                 >
//                   <option value="">--Select--</option>
//                   {saList.map((s) => (
//                     <option key={s._id} value={s._id}>
//                       {s.name}
//                     </option>
//                   ))}
//                 </select>
//               </SelectWrapper>
//             </div>
//           </div>

//           <div className="px-5 pb-5">
//             <Label>Caption</Label>
//             <input
//               type="text"
//               value={form.caption}
//               onChange={(e) => setFormField("caption", e.target.value)}
//               className={inputCls}
//               placeholder="Ad caption / description"
//             />
//           </div>
//         </div>

//         {/* Publication Items section */}
//         <div className="bg-white border border-blue-100 rounded-2xl shadow-sm overflow-hidden">
//           <div className="px-5 py-3 border-b border-blue-50 bg-blue-50/50 flex items-center justify-between">
//             <span className="text-xs font-semibold text-blue-700 uppercase tracking-widest flex items-center gap-2">
//               <RiNewspaperLine className="w-4 h-4 text-blue-500" />
//               Publication Items
//             </span>
//             <button
//               onClick={addItem}
//               className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold transition shadow-sm shadow-blue-200"
//             >
//               <RiAddLine className="w-3.5 h-3.5" /> Add Item
//             </button>
//           </div>

//           {form.items.length === 0 ? (
//             <div className="p-8 text-center text-slate-400">
//               <RiNewspaperLine className="w-12 h-12 mx-auto mb-3 opacity-50" />
//               <p className="text-sm">No items added yet</p>
//               <button
//                 onClick={addItem}
//                 className="mt-3 text-xs text-blue-600 hover:text-blue-700"
//               >
//                 Click here to add first item
//               </button>
//             </div>
//           ) : (
//             <>
//               <div className="flex items-center gap-1 px-5 pt-4 overflow-x-auto pb-0 border-b border-blue-100">
//                 {form.items.map((item, idx) => {
//                   const isActive = activeTab === idx;
//                   const label = `Item ${idx + 1}`;
//                   return (
//                     <div
//                       key={item.id}
//                       className={`group relative flex items-center gap-1.5 px-4 py-2 rounded-t-xl text-xs font-semibold cursor-pointer border-b-2 transition-all whitespace-nowrap select-none
//                         ${
//                           isActive
//                             ? "bg-white border-blue-600 text-blue-700 shadow-sm"
//                             : "bg-blue-50/60 border-transparent text-blue-400 hover:text-blue-600 hover:bg-blue-50"
//                         }`}
//                       onClick={() => setActiveTab(idx)}
//                     >
//                       <RiNewspaperLine className="w-3.5 h-3.5 flex-shrink-0" />
//                       <span>{label}</span>
//                       {form.items.length > 1 && (
//                         <button
//                           onClick={(e) => {
//                             e.stopPropagation();
//                             removeItem(item.id);
//                           }}
//                           className={`ml-1 rounded-full p-0.5 transition
//                             ${
//                               isActive
//                                 ? "text-red-400 hover:text-red-600 hover:bg-red-50"
//                                 : "text-blue-300 hover:text-red-400 opacity-0 group-hover:opacity-100"
//                             }`}
//                         >
//                           <RiCloseLine className="w-3 h-3" />
//                         </button>
//                       )}
//                     </div>
//                   );
//                 })}
//               </div>

//               {activeItem &&
//                 (() => {
//                   const itemWithCallback = {
//                     ...activeItem,
//                     _onChange: (key, val) => setItem(activeItem.id, key, val),
//                   };
//                   const sqcm =
//                     Number(activeItem.width || 0) *
//                     Number(activeItem.height || 0);

//                   return (
//                     <div className="p-5">
//                       <div className="grid grid-cols-3 gap-5">
//                         <div className="col-span-2 grid grid-cols-2 gap-4">
//                           <div>
//                             <Label>RO Number</Label>
//                             <input
//                               type="text"
//                               value={activeItem.roNumber}
//                               onChange={(e) =>
//                                 setItem(
//                                   activeItem.id,
//                                   "roNumber",
//                                   e.target.value,
//                                 )
//                               }
//                               className={inputCls}
//                               placeholder="e.g. NG/001(A)/25-26"
//                             />
//                             <div className="mt-1 text-[10px] text-blue-500">
//                               Format: NG/001(A)/25-26 (Auto-generated, editable
//                               if needed)
//                             </div>
//                           </div>

//                           <div>
//                             <Label>Media</Label>
//                             <SelectWrapper>
//                               <select
//                                 value={activeItem.media}
//                                 onChange={(e) =>
//                                   handleMediaChange(
//                                     activeItem.id,
//                                     e.target.value,
//                                   )
//                                 }
//                                 className={selectCls}
//                               >
//                                 <option value="">--Select--</option>
//                                 {media.map((m) => (
//                                   <option key={m._id} value={m._id}>
//                                     {m.organisation}
//                                   </option>
//                                 ))}
//                               </select>
//                             </SelectWrapper>
//                           </div>

//                           <div>
//                             <Label>Publication</Label>
//                             <input
//                               value={activeItem.publication}
//                               onChange={(e) =>
//                                 setItem(
//                                   activeItem.id,
//                                   "publication",
//                                   e.target.value,
//                                 )
//                               }
//                               className={inputCls}
//                               placeholder="Publication name"
//                             />
//                           </div>

//                           <div>
//                             <Label required>Edition</Label>
//                             <input
//                               value={activeItem.edition}
//                               onChange={(e) =>
//                                 setItem(
//                                   activeItem.id,
//                                   "edition",
//                                   e.target.value,
//                                 )
//                               }
//                               className={inputCls}
//                               placeholder="Edition (required)"
//                             />
//                           </div>

//                           <div>
//                             <Label>HUE</Label>
//                             <SelectWrapper>
//                               <select
//                                 value={activeItem.hue}
//                                 onChange={(e) =>
//                                   setItem(activeItem.id, "hue", e.target.value)
//                                 }
//                                 className={selectCls}
//                               >
//                                 <option>B/W</option>
//                                 <option>Color</option>
//                               </select>
//                             </SelectWrapper>
//                           </div>

//                           <div>
//                             <Label>Width (cm)</Label>
//                             <input
//                               type="number"
//                               min="0"
//                               step="0.01"
//                               value={activeItem.width}
//                               onChange={(e) =>
//                                 setItem(activeItem.id, "width", e.target.value)
//                               }
//                               className={inputCls}
//                               placeholder="0"
//                             />
//                           </div>

//                           <div>
//                             <Label>Height (cm)</Label>
//                             <input
//                               type="number"
//                               min="0"
//                               step="0.01"
//                               value={activeItem.height}
//                               onChange={(e) =>
//                                 setItem(activeItem.id, "height", e.target.value)
//                               }
//                               className={inputCls}
//                               placeholder="0"
//                             />
//                           </div>

//                           <div>
//                             <Label>Date of Publication</Label>
//                             <div className="relative">
//                               <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
//                               <input
//                                 type="date"
//                                 value={activeItem.dop}
//                                 onChange={(e) =>
//                                   setItem(activeItem.id, "dop", e.target.value)
//                                 }
//                                 className={`${inputCls} pl-9`}
//                               />
//                             </div>
//                           </div>

//                           <div>
//                             <Label>Page / Position</Label>
//                             <input
//                               value={activeItem.pagePosition}
//                               onChange={(e) =>
//                                 setItem(
//                                   activeItem.id,
//                                   "pagePosition",
//                                   e.target.value,
//                                 )
//                               }
//                               className={inputCls}
//                               placeholder="Page / Position"
//                             />
//                           </div>

//                           <div>
//                             <Label>Ad Type</Label>
//                             <SelectWrapper>
//                               <select
//                                 value={activeItem.adType}
//                                 onChange={(e) =>
//                                   handleAdTypeChange(
//                                     activeItem.id,
//                                     e.target.value,
//                                   )
//                                 }
//                                 className={selectCls}
//                                 disabled={!activeItem.media}
//                               >
//                                 <option value="">--Select Ad Type--</option>
//                                 {(adTypes[activeItem.media] || []).map(
//                                   (adType) => (
//                                     <option key={adType._id} value={adType._id}>
//                                       Type {adType.typeNumber} - ₹{adType.price}
//                                       /sq cm ({adType.names?.length || 0} names)
//                                     </option>
//                                   ),
//                                 )}
//                               </select>
//                             </SelectWrapper>

//                             {activeItem.adType && adTypes[activeItem.media] && (
//                               <div className="mt-3">
//                                 <div className="text-[10px] font-semibold text-blue-600 uppercase tracking-widest mb-2 flex items-center gap-2">
//                                   <RiListCheck2 className="w-3.5 h-3.5" />
//                                   Available Ad Names for Type{" "}
//                                   {
//                                     adTypes[activeItem.media].find(
//                                       (at) => at._id === activeItem.adType,
//                                     )?.typeNumber
//                                   }
//                                 </div>
//                                 <div className="flex flex-wrap gap-2">
//                                   {adTypes[activeItem.media]
//                                     .find((at) => at._id === activeItem.adType)
//                                     ?.names?.map((name, idx) => {
//                                       const selectedAdType = adTypes[
//                                         activeItem.media
//                                       ].find(
//                                         (at) => at._id === activeItem.adType,
//                                       );
//                                       return (
//                                         <button
//                                           key={idx}
//                                           type="button"
//                                           onClick={() =>
//                                             handleAdNameSelect(
//                                               activeItem.id,
//                                               name,
//                                               selectedAdType,
//                                             )
//                                           }
//                                           className={`text-xs px-3 py-1.5 rounded-lg transition ${
//                                             activeItem.selectedAdName === name
//                                               ? "bg-blue-600 text-white border border-blue-600 shadow-sm"
//                                               : "bg-white border border-blue-200 text-slate-700 hover:bg-blue-50"
//                                           }`}
//                                         >
//                                           {name}
//                                         </button>
//                                       );
//                                     })}
//                                 </div>
//                                 {activeItem.selectedAdName && (
//                                   <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-lg">
//                                     <div className="flex items-center justify-between">
//                                       <span className="text-xs font-semibold text-green-700">
//                                         Selected: {activeItem.selectedAdName}
//                                       </span>
//                                       <span className="text-sm font-bold text-green-700">
//                                         Rate: ₹{activeItem.rate}/sq cm
//                                       </span>
//                                     </div>
//                                   </div>
//                                 )}
//                               </div>
//                             )}

//                             {adTypes[activeItem.media]?.length === 0 &&
//                               activeItem.media && (
//                                 <p className="text-[10px] text-amber-500 mt-1">
//                                   No ad types available for this media
//                                 </p>
//                               )}
//                           </div>

//                           <div>
//                             <Label>Rate (Rs/Sq.cm)</Label>
//                             <input
//                               type="number"
//                               min="0"
//                               step="0.01"
//                               value={activeItem.rate}
//                               onChange={(e) =>
//                                 setItem(activeItem.id, "rate", e.target.value)
//                               }
//                               className={inputCls}
//                               placeholder="0"
//                             />
//                           </div>

//                           <div className="col-span-2">
//                             <div className="flex items-center gap-4 px-4 py-2.5 bg-blue-50/60 border border-blue-100 rounded-xl">
//                               <div className="flex items-center gap-2">
//                                 <span className="text-[10px] font-bold text-blue-500 uppercase tracking-widest">
//                                   Area
//                                 </span>
//                                 <span className="font-mono font-bold text-slate-700 text-sm">
//                                   {sqcm.toFixed(2)} sq.cm
//                                 </span>
//                               </div>
//                               <div className="w-px h-5 bg-blue-200" />
//                               <div className="flex items-center gap-2">
//                                 <span className="text-[10px] font-bold text-blue-500 uppercase tracking-widest">
//                                   Line Gross
//                                 </span>
//                                 <span className="font-mono font-bold text-blue-700 text-sm">
//                                   ₹{fmt(sqcm * Number(activeItem.rate || 0))}
//                                 </span>
//                               </div>
//                             </div>
//                           </div>

//                           {/* Per-Item Cheque Details */}
//                           <div className="col-span-2 border-t border-blue-100 pt-4 mt-2">
//                             <div className="flex items-center gap-2 mb-3">
//                               <RiBankLine className="w-4 h-4 text-blue-500" />
//                               <Label className="!mb-0">Cheque Details</Label>
//                               <span className="text-[10px] text-blue-400 ml-2">
//                                 Item {activeTab + 1}
//                               </span>
//                             </div>
//                             <div className="grid grid-cols-3 gap-4">
//                               <div>
//                                 <Label>Bank Name</Label>
//                                 <div className="relative">
//                                   <RiBankLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
//                                   <input
//                                     type="text"
//                                     value={
//                                       activeItem.chequeDetails?.bankName || ""
//                                     }
//                                     onChange={(e) =>
//                                       setItemChequeDetail(
//                                         activeItem.id,
//                                         "bankName",
//                                         e.target.value,
//                                       )
//                                     }
//                                     className={`${inputCls} pl-9`}
//                                     placeholder="e.g., HDFC Bank"
//                                   />
//                                 </div>
//                               </div>
//                               <div>
//                                 <Label>Cheque No.</Label>
//                                 <div className="relative">
//                                   <RiWalletLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
//                                   <input
//                                     type="text"
//                                     value={
//                                       activeItem.chequeDetails?.chequeNo || ""
//                                     }
//                                     onChange={(e) =>
//                                       setItemChequeDetail(
//                                         activeItem.id,
//                                         "chequeNo",
//                                         e.target.value,
//                                       )
//                                     }
//                                     className={`${inputCls} pl-9`}
//                                     placeholder="e.g., 123456"
//                                   />
//                                 </div>
//                               </div>
//                               <div>
//                                 <Label>Cheque Date</Label>
//                                 <div className="relative">
//                                   <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
//                                   <input
//                                     type="date"
//                                     value={
//                                       activeItem.chequeDetails?.chequeDate || ""
//                                     }
//                                     onChange={(e) =>
//                                       setItemChequeDetail(
//                                         activeItem.id,
//                                         "chequeDate",
//                                         e.target.value,
//                                       )
//                                     }
//                                     className={`${inputCls} pl-9`}
//                                   />
//                                 </div>
//                               </div>
//                             </div>
//                             <p className="text-[10px] text-blue-400 mt-2 italic">
//                               These fields are optional and can be left blank
//                             </p>
//                           </div>
//                         </div>

//                         <div className="col-span-1">
//                           <ItemFinancials item={itemWithCallback} />
//                         </div>
//                       </div>
//                     </div>
//                   );
//                 })()}
//             </>
//           )}
//         </div>

//         {/* Grand Total */}
//         {form.items.length > 1 && (
//           <div className="flex justify-end">
//             <div className="bg-white border border-blue-100 rounded-2xl shadow-sm overflow-hidden w-full max-w-md">
//               <div className="px-5 py-3 border-b border-blue-50 bg-blue-50/50">
//                 <span className="text-xs font-semibold text-blue-700 uppercase tracking-widest flex items-center gap-2">
//                   <RiCalculatorLine className="w-4 h-4 text-blue-500" />
//                   Grand Total — All Items
//                 </span>
//               </div>
//               <div className="p-5 space-y-2 text-sm">
//                 <div className="flex justify-between py-1.5 border-b border-blue-50">
//                   <span className="text-slate-600 font-medium">
//                     Total Gross
//                   </span>
//                   <span className="font-bold text-slate-800 font-mono">
//                     ₹{fmt(totals.gross)}
//                   </span>
//                 </div>
//                 <div className="flex justify-between text-xs text-slate-500">
//                   <span>Agency Commission</span>
//                   <span className="text-red-500 font-mono">
//                     − ₹{fmt(totals.agencyComm)}
//                   </span>
//                 </div>
//                 <div className="flex justify-between text-xs text-slate-500">
//                   <span>Other Discount</span>
//                   <span className="text-red-500 font-mono">
//                     − ₹{fmt(totals.otherDiscount)}
//                   </span>
//                 </div>
//                 <div className="flex justify-between text-xs text-slate-500">
//                   <span>Other Premium</span>
//                   <span className="text-green-500 font-mono">
//                     + ₹{fmt(totals.otherPremium)}
//                   </span>
//                 </div>
//                 <div className="flex justify-between text-xs text-slate-500">
//                   <span>Cash Discount</span>
//                   <span className="text-red-500 font-mono">
//                     − ₹{fmt(totals.cashDisc)}
//                   </span>
//                 </div>
//                 <div className="flex justify-between text-xs text-slate-500">
//                   <span>GST</span>
//                   <span className="text-emerald-600 font-mono">
//                     + ₹{fmt(totals.gstAmt)}
//                   </span>
//                 </div>
//                 <div className="flex items-center justify-between pt-3 border-t-2 border-blue-600/20 mt-1">
//                   <span className="text-blue-800 font-bold text-sm">
//                     Net Payable Amount
//                   </span>
//                   <span className="font-extrabold text-blue-700 text-lg font-mono">
//                     ₹{fmt(totals.net)}
//                   </span>
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}

//         <div className="flex justify-end gap-3">
//           <button
//             onClick={resetForm}
//             className="flex items-center gap-1.5 px-6 py-2.5 rounded-xl text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 font-semibold transition"
//           >
//             <RiCloseLine className="w-4 h-4" /> Reset
//           </button>

//           {lastCreatedRO && (
//             <button
//               onClick={() =>
//                 generateReleaseOrderPDF(lastCreatedRO, RO_IMAGE_BASE64)
//               }
//               className="flex items-center gap-2 px-7 py-2.5 rounded-xl text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white transition shadow-lg shadow-emerald-200"
//             >
//               <RiDownloadLine className="w-4 h-4" />
//               Download PDF
//             </button>
//           )}

//           <button
//             onClick={handleSubmit}
//             disabled={submitting || form.items.length === 0}
//             className="flex items-center gap-2 px-7 py-2.5 rounded-xl text-sm font-bold bg-blue-600 hover:bg-blue-700 text-white transition shadow-lg shadow-blue-200 disabled:opacity-60"
//           >
//             {submitting ? <Spinner /> : <RiCheckLine className="w-4 h-4" />}
//             {submitting ? "Creating…" : "Create Release Order"}
//           </button>
//         </div>
//       </div>

//       {toast && <Toast {...toast} onClose={() => setToast(null)} />}
//     </div>
//   );
// }
